<template>
  <div class="activity-management">
    <!-- 页面标题和操作按钮 -->
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title">活动管理</h2>
        <p class="page-subtitle">管理平台所有运动活动</p>
      </div>
      <div class="header-right">
        <el-button type="primary" @click="handleCreateActivity">
          <el-icon><Plus /></el-icon>
          创建活动
        </el-button>
        <el-button type="success" @click="handleCreateSportType">
          <el-icon><Plus /></el-icon>
          新增运动类型
        </el-button>
        <el-button @click="exportActivities">
          <el-icon><Download /></el-icon>
          导出数据
        </el-button>
      </div>
    </div>

    <!-- 筛选条件 -->
    <div class="filter-section">
      <el-card>
        <div class="filter-content">
          <!-- 快速筛选 -->
          <div class="quick-filters">
            <span class="filter-label">状态：</span>
            <el-radio-group v-model="filterParams.status" @change="handleFilterChange">
              <el-radio-button label="">全部</el-radio-button>
              <el-radio-button label="published">已发布</el-radio-button>
              <el-radio-button label="ongoing">进行中</el-radio-button>
              <el-radio-button label="completed">已完成</el-radio-button>
              <el-radio-button label="cancelled">已取消</el-radio-button>
            </el-radio-group>
          </div>

          <!-- 高级筛选 -->
          <el-form :inline="true" :model="filterParams" class="advanced-filters">
            <el-form-item label="运动类型">
              <el-select v-model="filterParams.sportType" placeholder="请选择运动类型" clearable @change="handleFilterChange">
                <el-option label="全部" value="" />
                <el-option v-for="type in sportTypes" :key="type.value" :label="type.label" :value="type.value" />
              </el-select>
            </el-form-item>

            <el-form-item label="活动时间">
              <el-date-picker
                v-model="filterParams.dateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                @change="handleFilterChange"
              />
            </el-form-item>

            <el-form-item label="参与人数">
              <el-input-number
                v-model="filterParams.minParticipants"
                :min="0"
                placeholder="最低"
                @change="handleFilterChange"
              />
              <span class="range-separator">-</span>
              <el-input-number
                v-model="filterParams.maxParticipants"
                :min="0"
                placeholder="最高"
                @change="handleFilterChange"
              />
            </el-form-item>

            <el-form-item label="场馆名称">
              <el-input
                v-model="filterParams.venue"
                placeholder="输入场馆名称筛选"
                prefix-icon="Search"
                clearable
                @input="handleSearch"
              />
            </el-form-item>

            <el-form-item>
              <el-input
                v-model="filterParams.keyword"
                placeholder="搜索活动名称/地点"
                prefix-icon="Search"
                clearable
                @input="handleSearch"
              />
            </el-form-item>

            <el-form-item>
              <el-button @click="resetFilters">重置</el-button>
            </el-form-item>
          </el-form>
        </div>
      </el-card>
    </div>

    <!-- 活动数据列表 -->
    <div class="data-section">
      <el-card>
        <!-- 活动统计卡片 -->
        <div class="stats-section">
          <el-row :gutter="20">
            <el-col :span="6" v-for="stat in activityStats" :key="stat.title">
              <div class="stat-card" :style="{ borderLeftColor: stat.color }">
                <div class="stat-content">
                  <div class="stat-value">{{ stat.value }}</div>
                  <div class="stat-title">{{ stat.title }}</div>
                </div>
                <div class="stat-icon" :style="{ color: stat.color }">
                  <el-icon><component :is="stat.icon" /></el-icon>
                </div>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- 活动列表表格 -->
        <el-table
          v-loading="loading"
          :data="activityList"
          style="width: 100%"
          @sort-change="handleSortChange"
        >
          <el-table-column prop="title" label="活动名称" sortable min-width="200">
            <template #default="scope">
              <span :class="{ 'text-primary': scope.row.isFeatured }">
                {{ scope.row.title }}
                <el-tag v-if="scope.row.isFeatured" type="primary" size="small" effect="light">推荐</el-tag>
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="sportType" label="运动类型" min-width="100">
            <template #default="scope">
              {{ getSportTypeLabel(scope.row.sportType) }}
            </template>
          </el-table-column>
          <el-table-column prop="venue_name" label="场馆名称" sortable min-width="130">
            <template #default="scope">
              <span v-if="scope.row.venue_name">{{ scope.row.venue_name }}</span>
              <span v-else class="text-placeholder">暂无</span>
            </template>
          </el-table-column>
          <el-table-column prop="location" label="活动地点" min-width="150" />
          <el-table-column prop="startTime" label="开始时间" sortable min-width="150">
            <template #default="scope">
              {{ formatDate(scope.row.startTime) }}
            </template>
          </el-table-column>
          <el-table-column prop="endTime" label="结束时间" sortable min-width="150">
            <template #default="scope">
              {{ formatDate(scope.row.endTime) }}
            </template>
          </el-table-column>
          <el-table-column prop="participants" label="参与人数" sortable width="100" />
          <el-table-column label="战队参赛" width="100">
            <template #default="scope">
              <span>{{ scope.row.teamParticipantCount || 0 }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="100">
            <template #default="scope">
              <el-tag :type="getStatusTagType(scope.row.status)">{{ getStatusLabel(scope.row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="180" fixed="right">
            <template #default="scope">
              <el-button size="small" type="primary" @click="handleEditActivity(scope.row)">编辑</el-button>
              <el-dropdown>
                <el-button size="small" type="text">
                  更多 <el-icon class="el-icon--right"><ArrowDown /></el-icon>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item @click="handleToggleFeatured(scope.row)">
                      <el-icon><Star /></el-icon>
                      {{ scope.row.isFeatured ? '取消推荐' : '设为推荐' }}
                    </el-dropdown-item>
                    <el-dropdown-item @click="handleToggleHidden(scope.row)">
                      <el-icon><View /></el-icon>
                      {{ scope.row.status === 'hidden' ? '取消隐藏' : '隐藏活动' }}
                    </el-dropdown-item>
                    <el-dropdown-item 
                      @click="handleDeleteActivity(scope.row)"
                      divided
                      class="text-danger"
                    >
                      <el-icon><Delete /></el-icon>
                      删除
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-section">
          <el-pagination
            v-model:current-page="pagination.current"
            v-model:page-size="pagination.size"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-card>
    </div>

    <!-- 创建/编辑活动弹窗 -->
    <ActivityDialog
      v-model="activityDialogVisible"
      :activityData="currentActivity"
      :mode="dialogMode"
      @success="handleDialogSuccess"
    />

    <!-- 新增运动类型对话框 -->
    <el-dialog
      v-model="sportTypeDialogVisible"
      title="新增运动类型"
      width="500px"
      class="new-sport-type-dialog"
    >
      <el-form
        ref="sportTypeFormRef"
        :model="newSportType"
        label-width="100px"
      >
        <el-form-item
          label="运动名称"
          prop="label"
          :rules="[{ required: true, message: '请输入运动类型名称', trigger: 'blur' }]"
        >
          <el-input
            v-model="newSportType.label"
            placeholder="请输入运动类型中文名称（如：篮球）"
            clearable
          />
        </el-form-item>
        <el-form-item
          label="运动英文名"
          prop="value"
          :rules="[{ required: true, message: '请输入运动类型值', trigger: 'blur' }]"
        >
          <el-input
            v-model="newSportType.value"
            placeholder="请输入运动类型英文值（如：basketball）"
            clearable
          />
          <div style="color: #909399; font-size: 12px; margin-top: 5px;">
            提示：类型值只能包含英文、数字和下划线，建议使用小写
          </div>
        </el-form-item>
        <el-form-item
          label="活动类型"
          prop="category"
          :rules="[{ required: true, message: '请选择活动类型', trigger: 'change' }]"
        >
          <el-select
            v-model="newSportType.category"
            placeholder="请选择活动类型"
            style="width: 100%"
          >
            <el-option label="球类运动" value="ballSports" />
            <el-option label="竞技竞速类" value="competitiveSports" />
            <el-option label="休闲类" value="leisureSports" />
            <el-option label="其他类" value="otherSports" />
          </el-select>
          <div style="color: #909399; font-size: 12px; margin-top: 5px;">
            提示：选择的活动类型将决定该运动在小程序发起页中的分类显示
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="sportTypeDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="saveNewSportType">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import api from '@/utils/api'
import axios from 'axios'
import ActivityDialog from '@/components/ActivityDialog.vue'

export default {
  name: 'ActivityManagement',
  components: {
    ActivityDialog
  },
  
  setup() {
    const loading = ref(false)
    const activityList = ref([])
    const searchTimer = ref(null) // 声明searchTimer变量
    
    // ActivityDialog相关
    const activityDialogVisible = ref(false)
    const currentActivity = ref(null)
    const dialogMode = ref('create') // 'create' 或 'edit'
    
    // 新增运动类型相关
    const sportTypeDialogVisible = ref(false)
    const newSportType = ref({ label: '', value: '', category: '' })
    
    // 运动类型选项 - 使用ref使其成为响应式数据
    const sportTypes = ref([])
    
    // 兜底默认运动类型（API 失败时使用）
    const defaultSportTypes = [
      { value: 'basketball', label: '篮球' },
      { value: 'football', label: '足球' },
      { value: 'running', label: '跑步' },
      { value: 'badminton', label: '羽毛球' },
      { value: 'tennis', label: '网球' },
      { value: 'swimming', label: '游泳' },
      { value: 'cycling', label: '骑行' },
      { value: 'pingpong', label: '乒乓球' },
      { value: 'fishing', label: '钓鱼' }
    ]
    
    // 从后端 API 加载运动类型（内置 + 自定义）
    const loadSportTypes = async () => {
      try {
        const res = await api.getSportTypes()
        if (res && res.success && Array.isArray(res.data) && res.data.length > 0) {
          sportTypes.value = res.data.map(t => ({ value: t.value, label: t.label, icon: t.icon, category: t.category, is_custom: t.is_custom, id: t.id }))
        } else {
          sportTypes.value = defaultSportTypes
        }
      } catch (error) {
        console.error('加载运动类型失败，使用默认值:', error)
        sportTypes.value = defaultSportTypes
      }
    }
    
    // 筛选参数 - 移除默认的参与人数限制，避免过滤掉大部分活动
    const filterParams = reactive({
      status: '',
      sportType: '',
      dateRange: [],
      minParticipants: null,
      maxParticipants: null,
      venue: '',
      keyword: ''
    })
    
    // 分页参数
    const pagination = reactive({
      current: 1,
      size: 10,
      total: 0
    })
    
    // 排序参数
    const sortParams = reactive({
      prop: '',
      order: ''
    })
    
    // 活动统计
    const activityStats = ref([
      { title: '总活动数', value: '0', icon: 'Calendar', color: '#409EFF' },
      { title: '进行中', value: '0', icon: 'Clock', color: '#E6A23C' },
      { title: '参与人数', value: '0', icon: 'User', color: '#67C23A' },
      { title: '总收入', value: '¥0', icon: 'Money', color: '#F56C6C' }
    ])
    
    // 更新统计数据
    const updateStats = (activities) => {
      if (!activities || activities.length === 0) {
        return;
      }
      
      const totalActivities = activities.length;
      const ongoingActivities = activities.filter(a => a.status === 'ongoing').length;
      const totalParticipants = activities.reduce((sum, a) => sum + (a.participants || 0), 0);
      const totalRevenue = activities.reduce((sum, a) => sum + (a.fee || 0), 0);
      
      activityStats.value = [
        { title: '总活动数', value: totalActivities.toString(), icon: 'Calendar', color: '#409EFF' },
        { title: '进行中', value: ongoingActivities.toString(), icon: 'Clock', color: '#E6A23C' },
        { title: '参与人数', value: totalParticipants.toString(), icon: 'User', color: '#67C23A' },
        { title: '总收入', value: `¥${totalRevenue}`, icon: 'Money', color: '#F56C6C' }
      ];
    }
    
    // 加载活动列表 - 完整版
    const loadActivities = async () => {
      loading.value = true
      try {
        // 构建查询参数
        const params = {
          page: pagination.current,
          limit: pagination.size, // 修正为后端API期望的参数名
          status: filterParams.status,
          sportType: filterParams.sportType,
          keyword: filterParams.keyword
        }

        // 场馆名称筛选
        if (filterParams.venue && filterParams.venue.trim()) {
          params.venue = filterParams.venue.trim()
        }
        
        // 只有当minParticipants有有效值时才添加到参数中
        if (filterParams.minParticipants && !isNaN(filterParams.minParticipants)) {
          params.minParticipants = parseInt(filterParams.minParticipants)
        }
        
        // 只有当maxParticipants有有效值时才添加到参数中
        if (filterParams.maxParticipants && !isNaN(filterParams.maxParticipants)) {
          params.maxParticipants = parseInt(filterParams.maxParticipants)
        }
        
        // 添加时间范围筛选
        if (filterParams.dateRange && filterParams.dateRange.length === 2) {
          params.startDate = filterParams.dateRange[0]
          params.endDate = filterParams.dateRange[1]
        }
        
        // 添加排序参数
        if (sortParams.prop && sortParams.order) {
          params.sortField = sortParams.prop
          params.sortOrder = sortParams.order
        }
        
        console.log('发送请求参数:', params);
        
        // 使用管理员API获取活动列表
        const response = await api.getActivities(params);
        
        // 处理API响应
        if (response && response.success) {
          // 直接使用API返回的活动数据，并添加sportType格式统一处理
          activityList.value = (response.data?.activities || []).map(activity => {
            // 统一sportType格式，确保与前端预期一致
            // 如果sportType是中文，转换为英文
            const sportTypeMapping = {
              '篮球': 'basketball',
              '足球': 'football',
              '跑步': 'running',
              '羽毛球': 'badminton',
              '网球': 'tennis',
              '游泳': 'swimming',
              '瑜伽': 'yoga',
              '骑行': 'cycling',
              '钓鱼': 'fishing'
            };
            
            // 检查并转换sportType
            let normalizedSportType = activity.sportType;
            if (typeof normalizedSportType === 'string' && sportTypeMapping[normalizedSportType]) {
              normalizedSportType = sportTypeMapping[normalizedSportType];
            }
            
            // 检查参与者数量类型，确保是数字
            let participants = activity.participants;
            if (typeof participants !== 'number' && participants !== undefined && participants !== null) {
              participants = parseInt(participants) || 0;
            }
            
            return {
              ...activity,
              sportType: normalizedSportType,
              participants: participants || 0
            };
          });
          
          // 更新总条数
          pagination.total = response.data?.total || 0;
          
          // 使用API返回的统计数据
          if (response.data?.stats) {
            activityStats.value = [
              { title: '总活动数', value: response.data.stats.totalActivities || '0', icon: 'Calendar', color: '#409EFF' },
              { title: '进行中', value: response.data.stats.ongoingActivities || '0', icon: 'Clock', color: '#E6A23C' },
              { title: '参与人数', value: response.data.stats.totalParticipants || '0', icon: 'User', color: '#67C23A' },
              { title: '总收入', value: '¥' + (response.data.stats.totalRevenue || 0), icon: 'Money', color: '#F56C6C' }
            ];
          } else {
            // 如果没有统计数据，使用本地计算
            updateStats(activityList.value);
          }
        } else {
          ElMessage.error(response?.message || '获取活动数据失败');
          activityList.value = [];
          pagination.total = 0;
        }
        
      } catch (error) {
        console.error('加载活动列表失败:', error);
        ElMessage.error('加载活动列表失败，请稍后重试');
        
        // 错误时提供默认模拟数据，确保页面能展示内容
        activityList.value = [
          {
            id: 1,
            title: '测试活动 - 篮球友谊赛',
            sportType: 'basketball',
            location: '测试篮球场',
            startTime: '2023-10-20 14:00:00',
            participants: 12,
            maxParticipants: 20,
            status: 'pending',
            fee: 0,
            isFeatured: true,
            description: '测试用篮球友谊赛',
            organizer: '测试管理员',
            contactPhone: '13800138000',
            createTime: '2023-10-15 09:30:00'
          }
        ];
        pagination.total = activityList.value.length;
        updateStats(activityList.value);
      } finally {
        loading.value = false;
      }
    }
    
    // 处理筛选条件变化
    const handleFilterChange = () => {
      pagination.current = 1
      loadActivities()
    }
    
    // 处理搜索
    const handleSearch = (val) => {
      if (searchTimer.value) {
        clearTimeout(searchTimer.value)
      }
      searchTimer.value = setTimeout(() => {
        pagination.current = 1
        loadActivities()
      }, 500)
    }
    
    // 处理搜索按钮点击
    const handleSearchClick = () => {
      pagination.current = 1
      loadActivities()
    }
    
    // 重置筛选条件
    const resetFilters = () => {
      filterParams.status = ''
      filterParams.sportType = ''
      filterParams.dateRange = []
      filterParams.minParticipants = null
      filterParams.maxParticipants = null
      filterParams.venue = ''
      filterParams.keyword = ''
      pagination.current = 1
      loadActivities()
    }
    
    // 处理排序变化
    const handleSortChange = ({ prop, order }) => {
      sortParams.prop = prop
      sortParams.order = order
      loadActivities()
    }
    
    // 处理页码变化
    const handleSizeChange = (size) => {
      pagination.size = size
      loadActivities()
    }
    
    // 处理每页条数变化
    const handleCurrentChange = (current) => {
      pagination.current = current
      loadActivities()
    }
    
    // 创建活动
    const handleCreateActivity = () => {
      dialogMode.value = 'create'
      currentActivity.value = null
      activityDialogVisible.value = true
    }
    
    // 编辑活动
    const handleEditActivity = (row) => {
      dialogMode.value = 'edit'
      currentActivity.value = { ...row }
      activityDialogVisible.value = true
    }
    
    // 处理对话框成功回调
    const handleDialogSuccess = () => {
      // 重新加载活动列表
      loadActivities()
      ElMessage.success(dialogMode.value === 'create' ? '活动创建成功' : '活动更新成功')
    }
    
    // 切换推荐状态
    const handleToggleFeatured = async (row) => {
      try {
        // 使用api.js中定义的toggleFeatured方法
        await api.toggleFeatured(row.id, !row.isFeatured)
        
        // 更新前端数据
        row.isFeatured = !row.isFeatured
        ElMessage.success(`${row.isFeatured ? '设为推荐' : '取消推荐'}成功`)
        
        // 重新加载统计数据
        loadActivities()
      } catch (error) {
        console.error('切换推荐状态失败:', error)
        ElMessage.error('操作失败，请重试')
      }
    }
    
    // 隐藏/取消隐藏活动
    const handleToggleHidden = async (row) => {
      const isHidden = row.status === 'hidden'
      const actionText = isHidden ? '取消隐藏' : '隐藏'
      try {
        await ElMessageBox.confirm(
          isHidden
            ? '取消隐藏后，该活动将在小程序中重新显示，确定吗？'
            : '隐藏后该活动将不在小程序中显示，仅管理后台可见，确定吗？',
          `确认${actionText}`,
          { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
        )
        await api.changeStatus(row.id, isHidden ? 'published' : 'hidden')
        row.status = isHidden ? 'published' : 'hidden'
        ElMessage.success(`${actionText}成功`)
        loadActivities()
      } catch (error) {
        if (error !== 'cancel') {
          console.error(`${actionText}失败:`, error)
          ElMessage.error(`${actionText}失败，请重试`)
        }
      }
    }

    // 改变活动状态
    const handleChangeStatus = async (row, status) => {
      try {
        // 使用api.js中定义的changeStatus方法
        await api.changeStatus(row.id, status)
        
        // 更新前端数据
        row.status = status
        ElMessage.success('操作成功')
        
        // 重新加载统计数据
        loadActivities()
      } catch (error) {
        console.error('改变活动状态失败:', error)
        ElMessage.error('操作失败，请重试')
      }
    }
    
    // 删除活动
    const handleDeleteActivity = async (row) => {
      try {
        await ElMessageBox.confirm(
          '确定要删除该活动吗？此操作不可撤销。',
          '确认删除',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        // 调用实际的API删除活动
        await api.deleteActivity(row.id)
        
        // 重新加载活动列表和统计数据
        loadActivities()
        
        ElMessage.success('删除成功')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除活动失败:', error)
          ElMessage.error('删除失败，请重试')
        }
      }
    }
    
    // 导出活动数据
    const exportActivities = async () => {
      try {
        // 这里应该是实际的API调用
        // const response = await api.get('/activities/export', {
        //   params: { ...filterParams },
        //   responseType: 'blob'
        // })
        
        // 模拟导出成功
        ElMessage.success('导出成功')
      } catch (error) {
        console.error('导出活动数据失败:', error)
        ElMessage.error('导出失败，请重试')
      }
    }
    
    // 打开新增运动类型对话框
    const handleCreateSportType = () => {
      newSportType.value = { label: '', value: '', category: '' }
      sportTypeDialogVisible.value = true
    }
    
    // 保存新增运动类型（调用真实 API）
    const saveNewSportType = async () => {
      if (!newSportType.value.label.trim() || !newSportType.value.value.trim() || !newSportType.value.category) {
        ElMessage.warning('请输入完整的运动类型名称、值和活动类型')
        return
      }
      
      try {
        const res = await api.createSportType({
          value: newSportType.value.value.trim().toLowerCase(),
          label: newSportType.value.label.trim(),
          icon: newSportType.value.icon || '🎯',
          category: newSportType.value.category
        })
        
        if (res && res.success) {
          ElMessage.success('运动类型添加成功')
          sportTypeDialogVisible.value = false
          // 重新加载运动类型列表
          await loadSportTypes()
        } else {
          ElMessage.error(res?.message || '添加失败')
        }
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || '添加运动类型失败'
        ElMessage.error(msg)
        console.error('新增运动类型失败:', error)
      }
    }
    
    // 组件挂载时加载数据
    onMounted(async () => {
      await loadSportTypes()
      loadActivities()
    })
    
    return {
      loading,
      activityList,
      filterParams,
      pagination,
      sortParams,
      activityStats,
      sportTypes,
      sportTypeDialogVisible,
      newSportType,
      activityDialogVisible,
      currentActivity,
      dialogMode,
      
      loadActivities,
      handleFilterChange,
      handleSearch,
      handleSearchClick,
      resetFilters,
      handleSortChange,
      handleSizeChange,
      handleCurrentChange,
      handleCreateActivity,
      handleEditActivity,
      handleDialogSuccess,
      handleToggleFeatured,
      handleToggleHidden,
      handleChangeStatus,
      handleDeleteActivity,
      exportActivities,
      handleCreateSportType,
      saveNewSportType,
      
      // 辅助函数
      getSportTypeLabel: (value) => {
        const type = sportTypes.value.find(t => t.value === value)
        return type ? type.label : value
      },
      formatDate: (date) => {
        if (!date) return ''
        return new Date(date).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      },
      getStatusLabel: (status) => {
        const statusMap = {
          published: '已发布',
          ongoing: '进行中',
          completed: '已完成',
          cancelled: '已取消'
        }
        return statusMap[status] || status
      },
      getStatusTagType: (status) => {
        const typeMap = {
          published: 'info',
          ongoing: 'success',
          completed: 'warning',
          cancelled: 'danger'
        }
        return typeMap[status] || 'info'
      }
    }
  }
}
</script>

<style lang="css">
/* 新增运动类型对话框样式 */
.new-sport-type-dialog .el-form {
  margin-top: 20px;
}

.new-sport-type-dialog .el-form-item {
  margin-bottom: 20px;
}

.new-sport-type-dialog .dialog-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 10px;
}

/* 空值占位样式 */
.text-placeholder {
  color: #c0c4cc;
}
</style>